import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { formatCompact } from "../../utils/formatCompact";
import { fetchAdminTransactionDetail } from "../api/fetchAdminTransaction";
import {
  formatTransactionAt,
  shortTransactionRef,
  transactionStatusMeta,
  transactionTypeLabel,
} from "../../users/userTransactionHelpers";

function DetailStat({ label, value }) {
  return (
    <div className="amf-desk-stat">
      <span className="amf-desk-stat-val">{value}</span>
      <span className="amf-desk-stat-key">{label}</span>
    </div>
  );
}

function Panel({ title, children, empty }) {
  return (
    <section className="atx-panel">
      <h3 className="atx-panel-title">{title}</h3>
      {empty ? <p className="atx-panel-empty">{empty}</p> : children}
    </section>
  );
}

function DlRow({ label, value, mono }) {
  if (value == null || value === "" || value === "—") return null;
  return (
    <div>
      <dt>{label}</dt>
      <dd className={mono ? "atx-mono" : undefined}>{value}</dd>
    </div>
  );
}

export default function TransactionFloorDetail({ tx }) {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    if (!tx?._id) {
      setDetail(null);
      setDetailError(null);
      return undefined;
    }

    let cancelled = false;
    setLoadingDetail(true);
    setDetailError(null);

    fetchAdminTransactionDetail(tx._id)
      .then((data) => {
        if (!cancelled) setDetail(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setDetail(null);
          setDetailError(
            err.response?.data?.error || err.message || "Failed to load transaction detail"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDetail(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tx?._id]);

  if (!tx) {
    return (
      <section className="amf-detail amf-detail-idle atx-detail" aria-label="Select a transaction">
        <div className="amf-idle">
          <p className="amf-idle-eyebrow">Transactions desk</p>
          <p className="amf-idle-title">Select a transaction from the ledger</p>
          <p className="amf-idle-copy">Deposits, withdrawals, and platform money movement.</p>
        </div>
      </section>
    );
  }

  const status = transactionStatusMeta(tx.status);
  const typeLabel = transactionTypeLabel(tx.transactionType);
  const full = detail?.transaction || tx;
  const user = detail?.user;
  const memberName = user?.name || tx.userName || "Member";
  const memberEmail = user?.email || tx.userEmail;

  return (
    <section className="amf-detail atx-detail" aria-label="Transaction detail">
      <header className="amf-desk amf-desk-with-question atx-desk-header">
        <div className="atx-desk-copy">
          <p className="amf-desk-eyebrow">{tx.currency || "CASH"}</p>
          <h2 className="amf-desk-title atx-desk-title">{typeLabel}</h2>
          <p className="amf-desk-meta">
            {memberName}
            {memberEmail ? ` · ${memberEmail}` : ""}
            {user?.inviteCode ? ` · ${user.inviteCode}` : ""}
          </p>
        </div>

        <div className="amf-desk-stats atx-desk-stats">
          <DetailStat label="Amount" value={formatCompact(Number(tx.amount) || 0)} />
          <DetailStat label="After" value={formatCompact(Number(tx.balanceAfter) || 0)} />
          <DetailStat label="Status" value={status.label} />
        </div>
      </header>

      <div className="amf-detail-scroll atx-detail-scroll">
        {loadingDetail ? (
          <div className="atx-detail-loading">
            <Loader2 size={18} className="am-spin" aria-hidden />
            <span>Loading detail…</span>
          </div>
        ) : null}

        {detailError ? (
          <div className="atx-error-banner" role="alert">
            <span>{detailError}</span>
          </div>
        ) : null}

        <div className="atx-detail-timing">
          <div className="atx-detail-time">
            <span className="atx-detail-time-key">Created</span>
            <span className="atx-detail-time-val">{formatTransactionAt(full.createdAt)}</span>
          </div>
          <div className="atx-detail-time">
            <span className="atx-detail-time-key">Method</span>
            <span className="atx-detail-time-val">{full.method || "Internal"}</span>
          </div>
          {full.transactionRef ? (
            <div className="atx-detail-time">
              <span className="atx-detail-time-key">Ref</span>
              <span className="atx-detail-time-val atx-mono">
                {shortTransactionRef(full.transactionRef) || full.transactionRef}
              </span>
            </div>
          ) : null}
          {full.updatedAt ? (
            <div className="atx-detail-time">
              <span className="atx-detail-time-key">Updated</span>
              <span className="atx-detail-time-val">{formatTransactionAt(full.updatedAt)}</span>
            </div>
          ) : null}
        </div>

        {!loadingDetail && !detailError ? (
          <>
            <Panel title="Balances">
              <dl className="atx-dl">
                <DlRow label="Cash amount" value={formatCompact(Number(full.cashAmount) || 0)} />
                <DlRow label="Before" value={formatCompact(Number(full.balanceBefore) || 0)} />
                <DlRow label="After" value={formatCompact(Number(full.balanceAfter) || 0)} />
                <DlRow label="Platform fee" value={formatCompact(Number(full.platformFee) || 0)} />
                <DlRow label="Other fees" value={formatCompact(Number(full.otherFees) || 0)} />
              </dl>
            </Panel>

            {(Number(full.loanAmountBorrowed) > 0 ||
              Number(full.loanAmountPaid) > 0 ||
              Number(full.loanInterestPaid) > 0) && (
              <Panel title="Loan">
                <dl className="atx-dl">
                  <DlRow label="Borrowed" value={formatCompact(full.loanAmountBorrowed)} />
                  <DlRow label="Principal paid" value={formatCompact(full.loanAmountPaid)} />
                  <DlRow label="Interest paid" value={formatCompact(full.loanInterestPaid)} />
                  <DlRow
                    label="Interest rate"
                    value={full.loanInterestRate ? `${full.loanInterestRate}%` : null}
                  />
                  <DlRow label="Interest balance" value={formatCompact(full.loanInterestBalance)} />
                </dl>
              </Panel>
            )}

            <Panel title="Payment">
              <dl className="atx-dl">
                <DlRow label="User payment" value={full.userPaymentMethod} />
                <DlRow label="Phone" value={full.phoneNumber} />
                <DlRow label="Withdrawal number" value={full.chosenOptionNumber} />
                <DlRow label="Admin phone" value={full.adminPhoneNumber} />
              </dl>
            </Panel>

            {user ? (
              <Panel title="Member balances">
                <dl className="atx-dl">
                  <DlRow label="CASH" value={formatCompact(user.CASH)} />
                  <DlRow label="COIN" value={formatCompact(user.COIN)} />
                </dl>
              </Panel>
            ) : null}

            {full.isDisputed || full.disputeReason ? (
              <Panel title="Dispute">
                <dl className="atx-dl">
                  <DlRow
                    label="Status"
                    value={full.disputeStatus || (full.isDisputed ? "Open" : null)}
                  />
                  <DlRow label="Reason" value={full.disputeReason} />
                  <DlRow
                    label="Resolved"
                    value={
                      full.disputeResolvedAt ? formatTransactionAt(full.disputeResolvedAt) : null
                    }
                  />
                </dl>
              </Panel>
            ) : null}

            {full.holdReason ? (
              <Panel title="Hold">
                <p className="atx-panel-copy">{full.holdReason}</p>
              </Panel>
            ) : null}

            {full.description ? (
              <Panel title="Note">
                <p className="atx-panel-copy">{full.description}</p>
              </Panel>
            ) : null}
          </>
        ) : null}
      </div>
    </section>
  );
}
